import React, { useContext, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import {
  FormSelect,
  FormSelectOption,
  Modal,
  Button,
  Pagination,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Thead, Tbody, Tr, Th, Td } from '@patternfly/react-table';
import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import { storesContext } from 'stores';
import { OrganizationManager } from 'types';
import './styles.scss';

type AdminUserRow = {
  preferred_name?: string;
  username?: string;
  default_email?: string;
  '@timestamp'?: string | number;
  org_manager_id?: string;
  doc_id: string;
  lms_order_number?: string;
  end_time?: string | number;
  subscriptionsDaysRemaining?: number;
};

type AssignManagerPayload = {
  assign: {
    manager_id: string;
    users: string[];
  };
};

type SortableColumn =
  | 'preferred_name'
  | 'username'
  | 'default_email'
  | '@timestamp'
  | 'manager'
  | 'lms_order_number'
  | 'end_time'
  | 'subscriptionsDaysRemaining';

const PER_PAGE_OPTIONS = [
  { title: '5', value: 5 },
  { title: '10', value: 10 },
  { title: '25', value: 25 },
];

const AdminUserList: React.FC = () => {
  const { organizationStore } = useContext(storesContext);
  const { i18n, t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [activeSortIndex, setActiveSortIndex] = useState(0);
  const [activeSortDirection, setActiveSortDirection] = useState<
    'asc' | 'desc'
  >('asc');

  const columnLabels: { key: SortableColumn; label: string }[] = [
    { key: 'preferred_name', label: t('Name') },
    { key: 'username', label: t('Username') },
    { key: 'default_email', label: t('Email') },
    { key: '@timestamp', label: t('Created') },
    { key: 'manager', label: t('Manager') },
    { key: 'lms_order_number', label: t('Order Number') },
    { key: 'end_time', label: t('End Date') },
    { key: 'subscriptionsDaysRemaining', label: t('Days Remaining') },
  ];

  const getManagerName = (row: AdminUserRow): string => {
    if (row.org_manager_id) {
      const foundManager = organizationStore.managerList.find(
        (manager: OrganizationManager) => manager.uuid === row.org_manager_id,
      );
      if (!foundManager) {
        return 'Unassign';
      }
      return foundManager.preferred_name || foundManager.username || 'Unassign';
    }
    return 'Unassign';
  };

  const dateFormatter = (ts?: string | number) => {
    if (ts === undefined || ts === null || ts === '') {
      return '';
    }
    return new Date(ts).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const promptAssignManager = async (
    managerId: string,
    clickedUserId: string,
  ) => {
    const tempUserSet = new Set<string>(
      Array.from(organizationStore.selectedUsersInAdminDB.values()) as string[],
    );
    tempUserSet.add(clickedUserId);

    const usersToAssign: AssignManagerPayload = {
      assign: {
        manager_id: managerId,
        users: Array.from(tempUserSet),
      },
    };

    if (usersToAssign.assign.users.length > 1) {
      const usersLength = usersToAssign.assign.users.length;
      organizationStore.newModal({
        title: t('Assign Manager'),
        text: t(`This will assign all {{usersLength}} users. Do you confirm?`, {
          usersLength,
        }),
        actionText: t('Yes'),
        onAction: () => {
          organizationStore.assignManager(managerId, usersToAssign);
          organizationStore.closeModal();
        },
      });
    } else {
      organizationStore.assignManager(managerId, usersToAssign);
    }
  };

  const getSortValue = (row: AdminUserRow, column: SortableColumn): string => {
    switch (column) {
      case 'manager':
        return getManagerName(row).toLowerCase();
      case '@timestamp':
      case 'end_time':
        return String(row[column] ?? '');
      case 'subscriptionsDaysRemaining':
        return String(row.subscriptionsDaysRemaining ?? '');
      default:
        return String(row[column] ?? '').toLowerCase();
    }
  };

  const matchesSearch = (row: AdminUserRow, term: string): boolean => {
    const haystack = [
      row.preferred_name,
      row.username,
      row.default_email,
      row.lms_order_number,
      getManagerName(row),
      dateFormatter(row['@timestamp']),
      dateFormatter(row.end_time),
      row.subscriptionsDaysRemaining,
    ]
      .filter((value) => value !== undefined && value !== null)
      .join(' ')
      .toLowerCase();

    return haystack.includes(term);
  };

  const filteredSortedUsers = useMemo(() => {
    const users = (organizationStore.userList || []) as AdminUserRow[];
    const term = searchValue.trim().toLowerCase();
    let data = term ? users.filter((row) => matchesSearch(row, term)) : users;

    const sortColumn = columnLabels[activeSortIndex]?.key;
    if (sortColumn) {
      data = [...data].sort((a, b) => {
        const aVal = getSortValue(a, sortColumn);
        const bVal = getSortValue(b, sortColumn);
        const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
        return activeSortDirection === 'asc' ? cmp : -cmp;
      });
    }

    return data;
  }, [
    organizationStore.userList,
    organizationStore.managerList,
    searchValue,
    activeSortIndex,
    activeSortDirection,
  ]);

  const pageUsers = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredSortedUsers.slice(start, start + perPage);
  }, [filteredSortedUsers, page, perPage]);

  const enabledManagers = useMemo(
    () =>
      (organizationStore.managerList as OrganizationManager[]).filter(
        (manager) => manager.state === 'enabled',
      ),
    [organizationStore.managerList],
  );

  const isRowSelected = (row: AdminUserRow) =>
    organizationStore.selectedUsersInAdminDB.has(row.doc_id);

  const areAllPageRowsSelected =
    pageUsers.length > 0 && pageUsers.every((row) => isRowSelected(row));

  const onSelectRow = (row: AdminUserRow, isSelecting: boolean) => {
    if (isSelecting) {
      organizationStore.addSelectedUserSubscription(row.doc_id);
    } else {
      organizationStore.removeSelectedUserSubscription(row.doc_id);
    }
  };

  const onSelectAllPageRows = (isSelecting: boolean) => {
    if (isSelecting) {
      organizationStore.addAllSelectedUsersSubscriptions(
        pageUsers.map((row) => row.doc_id),
      );
    } else {
      pageUsers.forEach((row) => {
        organizationStore.removeSelectedUserSubscription(row.doc_id);
      });
    }
  };

  const renderManagerSelect = (row: AdminUserRow) => {
    const isAssignedToEnabledManager = enabledManagers.some(
      (manager) => manager.uuid === row.org_manager_id,
    );
    const managerVal = isAssignedToEnabledManager
      ? row.org_manager_id
      : 'Unassign';

    return (
      <FormSelect
        id={`manager-select-${row.doc_id}`}
        aria-label={t('Manager')}
        value={managerVal}
        onChange={(_event, value) => {
          promptAssignManager(value, row.doc_id);
        }}
      >
        {enabledManagers.map((manager) => (
          <FormSelectOption
            key={manager.uuid}
            value={manager.uuid}
            label={manager.preferred_name || manager.username || ''}
          />
        ))}
        <FormSelectOption value="Unassign" label="Unassign" />
      </FormSelect>
    );
  };

  const renderCell = (column: SortableColumn, row: AdminUserRow) => {
    switch (column) {
      case '@timestamp':
        return dateFormatter(row['@timestamp']);
      case 'end_time':
        return dateFormatter(row.end_time);
      case 'manager':
        return renderManagerSelect(row);
      case 'subscriptionsDaysRemaining':
        return row.subscriptionsDaysRemaining ?? '';
      default:
        return row[column] ?? '';
    }
  };

  return (
    <React.Fragment>
      <Toolbar id="admin-user-list-toolbar">
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              className="user-search-bar"
              placeholder={t('Search')}
              aria-label={t('Search users')}
              value={searchValue}
              onChange={(_event, value) => {
                setSearchValue(value);
                setPage(1);
              }}
              onClear={() => {
                setSearchValue('');
                setPage(1);
              }}
            />
          </ToolbarItem>
          <ToolbarItem variant="pagination" align={{ default: 'alignRight' }}>
            <Pagination
              itemCount={filteredSortedUsers.length}
              page={page}
              perPage={perPage}
              perPageOptions={PER_PAGE_OPTIONS}
              onSetPage={(_event, newPage) => setPage(newPage)}
              onPerPageSelect={(_event, newPerPage, newPage) => {
                setPerPage(newPerPage);
                setPage(newPage);
              }}
              variant="top"
              isCompact
              widgetId="admin-user-list-pagination"
            />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>
      <Table
        className="admin-user-list-table"
        aria-label={t('Users')}
        variant="compact"
      >
        <Thead>
          <Tr>
            <Th
              select={{
                onSelect: (_event, isSelecting) =>
                  onSelectAllPageRows(isSelecting),
                isSelected: areAllPageRowsSelected,
              }}
              aria-label={t('Select all users')}
            />
            {columnLabels.map((column, index) => (
              <Th
                key={column.key}
                sort={{
                  sortBy: {
                    index: activeSortIndex + 1,
                    direction: activeSortDirection,
                    defaultDirection: 'asc',
                  },
                  onSort: (_event, _columnIndex, direction) => {
                    setActiveSortIndex(index);
                    setActiveSortDirection(direction);
                  },
                  columnIndex: index + 1,
                }}
              >
                {column.label}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {pageUsers.length === 0 ? (
            <Tr>
              <Td colSpan={columnLabels.length + 1}>
                {t('No Users Available')}
              </Td>
            </Tr>
          ) : (
            pageUsers.map((row, rowIndex) => (
              <Tr key={row.doc_id || row.username}>
                <Td
                  select={{
                    rowIndex,
                    onSelect: (_event, isSelecting) =>
                      onSelectRow(row, isSelecting),
                    isSelected: isRowSelected(row),
                  }}
                />
                {columnLabels.map((column) => (
                  <Td key={column.key} dataLabel={column.label}>
                    {renderCell(column.key, row)}
                  </Td>
                ))}
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
      <Modal
        variant="small"
        title={organizationStore.modal.title}
        isOpen={organizationStore.modal.opened}
        onClose={organizationStore.closeModal}
        actions={[
          <Button
            key="confirm"
            variant="primary"
            onClick={organizationStore.modal.onAction}
          >
            {organizationStore.modal.actionText}
          </Button>,
        ]}
      >
        {organizationStore.modal.text}
      </Modal>
    </React.Fragment>
  );
};

export default withErrorBoundary(
  observer(AdminUserList),
  GenericViewError,
  handleError,
);
